/**
 * Site-wide design.
 *
 * The full theme model, in owner language. Deliberately no schema jargon:
 * a volunteer choosing colours should read "Hoofdkleur", not "primary role",
 * and "Rustig / Normaal / Ruim" rather than "density: compact | normal |
 * spacious".
 *
 * Every control writes one bounded field. There is no free-form input here at
 * all except the colour pickers, which are constrained to `#rrggbb` by the
 * native control and re-validated by the server.
 */
import React from 'react';
import { ArrowLeft } from 'lucide-react';

import { cn } from '@/lib/utils';

import { contrastRatio, resolvePalette } from '../render/tokens';
import type { SiteChrome } from '../types';
import { Intro, Note, Segmented } from './ChromeInspector';

type Theme = SiteChrome['theme'];

const COLOR_LABELS: Array<[keyof Theme['colors'], string, string]> = [
  ['primary', 'Hoofdkleur', 'Knoppen, links en accenten'],
  ['secondary', 'Tweede kleur', 'Donkere vlakken en secundaire knoppen'],
  ['accent', 'Accentkleur', 'Highlights en vinkjes'],
  ['background', 'Paginakleur', 'De achtergrond van je pagina'],
  ['surface', 'Vlakken', 'Kaarten en lichte blokken'],
  ['text', 'Tekst', 'Gewone tekstkleur'],
  ['muted', 'Gedempte tekst', 'Bijschriften en toelichting'],
  ['dark', 'Donkere band', 'Donkere secties en de footer'],
];

export function DesignPanel({
  site,
  onUpdateTheme,
  onBack,
}: {
  site: SiteChrome;
  onUpdateTheme: (patch: Partial<Theme>) => void;
  onBack: () => void;
}) {
  const theme = site.theme;
  const palette = resolvePalette(theme);

  // Measured, not guessed. A palette where body text fails against the page
  // background is the single most common way a well-meant colour choice makes
  // a site unreadable, and the owner cannot be expected to compute it.
  const bodyContrast = contrastRatio(palette.background, palette.text);
  const primaryOnBackground = contrastRatio(palette.background, palette.primary);

  const setColors = (patch: Partial<Theme['colors']>) =>
    onUpdateTheme({ colors: { ...theme.colors, ...patch } });

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 transition hover:text-slate-800"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Terug naar pagina
      </button>

      <Intro
        title="Ontwerp van je website"
        description="Deze keuzes gelden voor alle pagina's. Secties volgen ze automatisch."
      />

      <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-white/10 px-4 pb-3 pt-4" style={{ background: palette.dark, color: palette.light }}>
          <div className="flex items-center justify-between gap-3 text-[10px] font-bold uppercase tracking-[0.14em] opacity-75">
            <span>{site.settings.siteName || 'Jouw website'}</span>
            <span>Live stijl</span>
          </div>
          <div className="mt-7 max-w-[18rem]">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em]" style={{ color: palette.accent }}>Samen dichtbij</p>
            <p className="mt-2 text-2xl font-bold leading-[0.98]" style={{ fontFamily: 'var(--zs-font-heading)' }}>Een plek die blijft hangen.</p>
            <p className="mt-2 text-xs leading-relaxed opacity-75">Kleuren, lettertypes en vormen vormen samen één herkenbaar verhaal.</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="inline-flex items-center px-3 py-1.5 text-[10px] font-bold" style={{ background: palette.accent, color: palette.dark, borderRadius: radiusPreview(theme.buttons.radius) }}>Bekijk stijl</span>
              <span className="inline-flex items-center border px-3 py-1.5 text-[10px] font-bold" style={{ borderColor: 'rgba(255,255,255,.45)', color: palette.light, borderRadius: radiusPreview(theme.buttons.radius) }}>Meer weten</span>
            </div>
          </div>
          <div className="mt-7 grid grid-cols-3 gap-2">
            {['Kleur', 'Type', 'Vorm'].map((label, index) => <div key={label} className="rounded-lg p-2" style={{ background: index === 0 ? palette.primary : 'rgba(255,255,255,.1)' }}><span className="block text-[9px] font-bold uppercase tracking-wide opacity-70">{label}</span><span className="mt-1 block text-[10px] font-semibold">{index === 0 ? 'Herkenbaar' : index === 1 ? 'Helder' : 'Zacht'}</span></div>)}
          </div>
        </div>
        <div className="grid grid-cols-4 gap-1.5 border-t border-slate-100 px-4 py-3">
          {COLOR_LABELS.slice(0, 4).map(([role, label]) => <div key={role} title={label} className="h-8 rounded-lg ring-1 ring-inset ring-black/5" style={{ background: theme.colors[role] }} />)}
        </div>
      </section>

      <section className="space-y-3">
        <h4 className="text-sm font-bold text-slate-900">Kleuren</h4>
        <div className="space-y-2">
          {COLOR_LABELS.map(([role, label, hint]) => (
            <label key={role} className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2">
              <input
                type="color"
                value={theme.colors[role]}
                onChange={(event) => setColors({ [role]: event.target.value } as Partial<Theme['colors']>)}
                aria-label={label}
                className="h-8 w-8 shrink-0 cursor-pointer rounded border-0 bg-transparent p-0"
              />
              <span className="min-w-0 flex-1">
                <span className="block text-sm font-semibold text-slate-800">{label}</span>
                <span className="block truncate text-xs text-slate-500">{hint}</span>
              </span>
              <span className="font-mono text-[10px] text-slate-400">{theme.colors[role]}</span>
            </label>
          ))}
        </div>

        {bodyContrast < 4.5 ? (
          <Note>
            De tekstkleur heeft te weinig contrast met de paginakleur
            ({bodyContrast.toFixed(1)}:1). Voor goed leesbare tekst is 4,5:1 nodig.
          </Note>
        ) : null}
        {primaryOnBackground < 3 ? (
          <Note>
            De hoofdkleur valt weinig op tegen de paginakleur. Links en knoppen kunnen
            daardoor lastig te herkennen zijn.
          </Note>
        ) : null}
      </section>

      <section className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Typografie</h4>
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs leading-relaxed text-slate-600">Deze fontset geldt voor de hele website: navigatie, koppen, teksten, kaarten en footer.</p>
        <FontPicker
          value={theme.typography.pairing}
          onChange={(pairing) => onUpdateTheme({ typography: { ...theme.typography, pairing } })}
        />
        <Segmented
          label="Grootte koppen"
          value={theme.typography.headingScale}
          options={[['sm', 'Compact'], ['md', 'Normaal'], ['lg', 'Groot']]}
          onChange={(value) => onUpdateTheme({ typography: { ...theme.typography, headingScale: value as 'sm' | 'md' | 'lg' } })}
        />
        <Segmented
          label="Grootte tekst"
          value={theme.typography.bodyScale}
          options={[['sm', 'Compact'], ['md', 'Normaal'], ['lg', 'Groot']]}
          onChange={(value) => onUpdateTheme({ typography: { ...theme.typography, bodyScale: value as 'sm' | 'md' | 'lg' } })}
        />
      </section>

      <section className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Knoppen</h4>
        <ButtonPreview theme={theme} palette={palette} />
        <Segmented
          label="Vorm"
          value={theme.buttons.radius}
          options={[['sharp', 'Recht'], ['subtle', 'Subtiel'], ['rounded', 'Rond'], ['soft', 'Zacht']]}
          onChange={(value) => onUpdateTheme({ buttons: { ...theme.buttons, radius: value as Theme['buttons']['radius'] } })}
        />
        <Segmented
          label="Standaardgrootte"
          value={theme.buttons.size}
          options={[['normal', 'Normaal'], ['large', 'Groot']]}
          onChange={(value) => onUpdateTheme({ buttons: { ...theme.buttons, size: value as 'normal' | 'large' } })}
        />
      </section>

      <section className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Visuele bouwstenen</h4>
        <p className="text-xs leading-relaxed text-slate-500">Zo werken vlakken, afbeeldingen en schaduwen samen in je website.</p>
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-xl p-3" style={{ background: palette.surface, borderRadius: radiusPreview(theme.shapes.radius), boxShadow: shadowPreview(theme.shapes.shadow) }}>
            <span className="block h-12 rounded-lg" style={{ background: `linear-gradient(135deg, ${palette.primary}, ${palette.accent})`, borderRadius: radiusPreview(theme.shapes.imageRadius) }} />
            <span className="mt-2 block text-xs font-bold" style={{ color: palette.text }}>Opvallend vlak</span>
            <span className="mt-1 block text-[10px]" style={{ color: palette.muted }}>Beeld + tekst</span>
          </div>
          <div className="rounded-xl p-3" style={{ background: palette.background, borderRadius: radiusPreview(theme.shapes.radius), boxShadow: shadowPreview(theme.shapes.shadow) }}>
            <span className="block h-12 rounded-lg" style={{ background: palette.dark, opacity: 0.92, borderRadius: radiusPreview(theme.shapes.imageRadius) }} />
            <span className="mt-2 block text-xs font-bold" style={{ color: palette.text }}>Diepe band</span>
            <span className="mt-1 block text-[10px]" style={{ color: palette.muted }}>Contrast + ritme</span>
          </div>
        </div>
        <div className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5">
          <span className="text-xs font-semibold text-slate-700">Huidige combinatie</span>
          <span className="text-[11px] text-slate-500">{theme.shapes.radius} · {theme.shapes.shadow} schaduw</span>
        </div>
      </section>

      <section className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Vormen</h4>
        <Segmented
          label="Afronding vlakken"
          value={theme.shapes.radius}
          options={[['sharp', 'Recht'], ['subtle', 'Subtiel'], ['rounded', 'Rond'], ['soft', 'Zacht']]}
          onChange={(value) => onUpdateTheme({ shapes: { ...theme.shapes, radius: value as Theme['shapes']['radius'] } })}
        />
        <Segmented
          label="Afronding afbeeldingen"
          value={theme.shapes.imageRadius}
          options={[['sharp', 'Recht'], ['subtle', 'Subtiel'], ['rounded', 'Rond'], ['soft', 'Zacht']]}
          onChange={(value) => onUpdateTheme({ shapes: { ...theme.shapes, imageRadius: value as Theme['shapes']['imageRadius'] } })}
        />
        <Segmented
          label="Schaduw"
          value={theme.shapes.shadow}
          options={[['none', 'Geen'], ['subtle', 'Subtiel'], ['elevated', 'Duidelijk']]}
          onChange={(value) => onUpdateTheme({ shapes: { ...theme.shapes, shadow: value as Theme['shapes']['shadow'] } })}
        />
      </section>

      <section className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Ruimte</h4>
        <Segmented
          label="Breedte van de inhoud"
          value={theme.layout.contentWidth}
          options={[['compact', 'Smal'], ['normal', 'Normaal'], ['wide', 'Breed']]}
          onChange={(value) => onUpdateTheme({ layout: { ...theme.layout, contentWidth: value as Theme['layout']['contentWidth'] } })}
        />
        <Segmented
          label="Uitlijning pagina"
          value={theme.layout.pageWidth ?? 'inset'}
          options={[['inset', 'Ingesprongen'], ['nav', 'Gelijk aan navigatie']]}
          onChange={(value) => onUpdateTheme({ layout: { ...theme.layout, pageWidth: value as NonNullable<Theme['layout']['pageWidth']> } })}
        />
        <Segmented
          label="Dichtheid"
          value={theme.layout.density}
          options={[['compact', 'Compact'], ['normal', 'Normaal'], ['spacious', 'Ruim']]}
          onChange={(value) => onUpdateTheme({ layout: { ...theme.layout, density: value as Theme['layout']['density'] } })}
        />
      </section>

      <section className="space-y-3 border-t border-slate-100 pt-5">
        <h4 className="text-sm font-bold text-slate-900">Beweging</h4>
        <Segmented
          label="Animaties"
          value={theme.motion}
          options={[['normal', 'Normaal'], ['minimal', 'Minimaal']]}
          onChange={(value) => onUpdateTheme({ motion: value as Theme['motion'] })}
        />
        <p className="text-xs text-slate-500">
          Bezoekers die in hun systeem minder beweging hebben ingesteld, krijgen altijd de
          rustige variant — ongeacht deze keuze.
        </p>
      </section>
    </div>
  );
}

const PAIRINGS: Array<[Theme['typography']['pairing'], string, string, string]> = [
  ['modern', 'Modern', 'Strak en helder', '"Inter", system-ui, sans-serif'],
  ['humanist', 'Warm', 'Vriendelijk en open', '"Segoe UI", Candara, sans-serif'],
  ['editorial', 'Redactioneel', 'Serif kop, strakke tekst', 'Georgia, serif'],
  ['classic', 'Klassiek', 'Tijdloos en formeel', '"Palatino Linotype", Georgia, serif'],
];

/** Font choices shown in their own typeface, because that is the only way the
 * choice means anything to someone who does not know the font names. */
function FontPicker({
  value,
  onChange,
}: {
  value: Theme['typography']['pairing'];
  onChange: (pairing: Theme['typography']['pairing']) => void;
}) {
  return (
    <div>
    <span className="block text-sm font-semibold text-slate-800">Fontset voor de hele website</span>
      <div role="radiogroup" aria-label="Fontcombinatie" className="mt-2 grid grid-cols-2 gap-2">
        {PAIRINGS.map(([pairing, label, hint, stack]) => (
          <button
            key={pairing}
            type="button"
            role="radio"
            aria-checked={value === pairing}
            onClick={() => onChange(pairing)}
            className={cn(
              'rounded-xl border p-2.5 text-left transition',
              value === pairing
                ? 'border-teal-400 bg-teal-50/60 ring-1 ring-teal-300'
                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50',
            )}
          >
            <span className="block text-lg font-bold leading-tight text-slate-900" style={{ fontFamily: stack }}>Samen dichtbij</span>
            <span className="mt-1 block text-[11px] leading-snug text-slate-500" style={{ fontFamily: stack }}>Een heldere plek voor iedereen.</span>
            <span className="mt-1 block text-xs font-semibold text-slate-700">{label}</span>
            <span className="block truncate text-[11px] text-slate-500">{hint}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

function ButtonPreview({ theme, palette }: { theme: Theme; palette: ReturnType<typeof resolvePalette> }) {
  const radius = radiusPreview(theme.buttons.radius);
  const base = 'inline-flex items-center justify-center px-3 py-2 text-[10px] font-bold transition';
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <span className="mb-2 block text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">Voorbeeldstijlen</span>
      <div className="flex flex-wrap items-center gap-2">
        <span className={base} style={{ background: palette.primary, color: palette.light, borderRadius: radius }}>Vullen</span>
        <span className={base} style={{ border: `1px solid ${palette.primary}`, color: palette.primary, borderRadius: radius }}>Omlijnd</span>
        <span className={`${base} px-1`} style={{ color: palette.primary }}>Tekst <span aria-hidden="true" className="ml-1">→</span></span>
      </div>
    </div>
  );
}

function radiusPreview(value: Theme['buttons']['radius'] | Theme['shapes']['radius'] | Theme['shapes']['imageRadius']): string {
  return ({ sharp: '0px', subtle: '6px', rounded: '14px', soft: '24px' }[value] ?? '6px');
}

function shadowPreview(value: Theme['shapes']['shadow']): string {
  return ({ none: 'none', subtle: '0 8px 24px rgba(15,23,42,.08)', elevated: '0 14px 34px rgba(15,23,42,.16)' }[value] ?? 'none');
}
